import { useMemo } from "react";
import { useTable } from "react-table";
import primaryComponents from "../../../../primaryComponents";
import styles from "./styles.module.scss";
import { DeleteIcon } from "../../../../../assets/svgIcons";
import secondaryComponents from "../../..";
import { formatCurrency } from "../../../../../helpers";
import { useSelector } from "react-redux";

export const useProducts = ({
  pOForm,
  setPOForm = () => {},
  ...validationParams
}) => {
  const {
    control,
    register,
    trigger,
    formState: { errors },
    setValue,
    clearErrors,
  } = validationParams;
  const { storeList } = useSelector((state) => state.profileDetailsReducer);

  const storeMap = useMemo(() => {
    return (storeList || []).reduce((acc, store) => {
      acc[store.id] = store;
      return acc;
    }, {});
  }, [storeList]);

  const updateItem = (index, key, value) => {
    setPOForm((prev) => {
      const updatedItems = [...(prev.items || [])];
      updatedItems[index] = {
        ...updatedItems[index],
        [key]: value,
      };
      setValue("items", updatedItems);
      setValue(`items.${index}.${key}`, value);
      return { ...prev, items: updatedItems };
    });
  };

  const handleCostPriceChange = (index, value) => {
    const num = Number(value || 0);
    setPOForm((prev) => {
      const updatedItems = [...(prev.items || [])];
      if (!updatedItems[index]) return prev;
      updatedItems[index] = {
        ...updatedItems[index],
        cost_price: value,
        base_cost_price: num,
      };
      setValue("items", updatedItems);
      setValue(`items.${index}.cost_price`, value);
      return { ...prev, items: updatedItems };
    });
  };

  const handleSellingPriceChange = (index, value) => {
    const num = Number(value || 0);
    setPOForm((prev) => {
      const updatedItems = [...(prev.items || [])];
      if (!updatedItems[index]) return prev;
      updatedItems[index] = {
        ...updatedItems[index],
        selling_price: value,
        base_selling_price: num,
        percentage: "",
      };
      setValue("items", updatedItems);
      setValue(`items.${index}.selling_price`, value);
      setValue(`items.${index}.percentage`, "");
      return { ...prev, items: updatedItems };
    });
  };

  const handlePercentageChange = (index, percentValue) => {
    setPOForm((prev) => {
      const updatedItems = [...(prev.items || [])];
      const item = updatedItems[index];
      if (!item) return prev;

      const baseSelling = Number(
        item.base_selling_price ?? item.selling_price ?? 0,
      );
      const baseCost = Number(item.base_cost_price ?? item.cost_price ?? 0);
      const baseToUse = baseSelling > 0 ? baseSelling : baseCost;
      const pct = percentValue === "" ? null : Number(percentValue);

      let newSelling = baseSelling;

      if (pct !== null && !isNaN(pct)) {
        newSelling = Number((baseToUse * (1 + pct / 100)).toFixed(2));
      }

      updatedItems[index] = {
        ...item,
        percentage: percentValue,
        selling_price: newSelling,
      };

      setValue("items", updatedItems);
      setValue(`items.${index}.percentage`, percentValue);
      setValue(`items.${index}.selling_price`, newSelling);

      return { ...prev, items: updatedItems };
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Product Name",
        accessor: "product_name",
        Cell: ({ cell: { value }, row }) => (
          <span className="d-flex align-items-center">
            <span
              className="mr-2 cursor-pointer"
              onClick={() => deleteRow(row.index)}
            >
              <DeleteIcon />
            </span>

            <secondaryComponents.ConcatenateWithTooltip
              value={value}
              noOfCharacters={200}
            />
          </span>
        ),
      },

      {
        Header: "Store",
        accessor: "store_id",
        Cell: ({ cell: { value } }) => storeMap[value]?.name || "N/A",
      },

      { Header: "Available QTY", accessor: "available_quantity" },

      {
        Header: "Request QTY",
        accessor: "requested_quantity",
        Cell: ({ cell: { value }, row }) => (
          <primaryComponents.InputFieldV2
            control={control}
            name={`items.${row.index}.requested_quantity`}
            register={register}
            value={value}
            placeholder="Enter QTY"
            classNames={`white ${styles.inputField}`}
            type="number"
            step={0.01}
            min={0.01}
            floatError={false}
            errorMessage={errors?.items?.[row.index]?.requested_quantity}
            onKeyUp={() =>
              errors?.items?.[row.index]?.requested_quantity &&
              trigger(`items.${row.index}.requested_quantity`)
            }
            onChange={(e) =>
              updateItem(row.index, "requested_quantity", e.target.value)
            }
          />
        ),
      },

      {
        Header: "Unit",
        accessor: "unit",
        Cell: ({ cell: { value } }) => value || "N/A",
      },

      {
        Header: "Unit Cost Price",
        accessor: "cost_price",
        Cell: ({ cell: { value }, row }) => {
          return (
            <primaryComponents.AmountFieldV2
              control={control}
              name={`items.${row.index}.cost_price`}
              register={register}
              value={value}
              placeholder="Enter cost price"
              classNames={`white ${styles.inputField}`}
              currency_code={storeMap?.[row?.original?.store_id]?.currency_code}
              floatError={false}
              errorMessage={errors?.items?.[row.index]?.cost_price}
              onKeyUp={() =>
                errors?.items?.[row.index]?.cost_price &&
                trigger(`items.${row.index}.cost_price`)
              }
              onChange={(e) => handleCostPriceChange(row.index, e.target.value)}
            />
          );
        },
      },

      {
        Header: "% Adjust",
        accessor: "percentage",
        Cell: ({ cell: { value }, row }) => {
          return (
            <primaryComponents.InputFieldV2
              control={control}
              name={`items.${row.index}.percentage`}
              register={register}
              value={value ?? ""}
              placeholder="e.g. 10"
              classNames={`white ${styles.inputField}`}
              type="number"
              step={0.01}
              floatError={false}
              onChange={(e) =>
                handlePercentageChange(row.index, e.target.value)
              }
            />
          );
        },
      },

      {
        Header: "Unit Selling Price",
        accessor: "selling_price",
        Cell: ({ cell: { value }, row }) => {
          return (
            <primaryComponents.AmountFieldV2
              control={control}
              name={`items.${row.index}.selling_price`}
              register={register}
              value={value}
              placeholder="Enter selling price"
              classNames={`white ${styles.inputField}`}
              currency_code={storeMap?.[row?.original?.store_id]?.currency_code}
              floatError={false}
              onChange={(e) =>
                handleSellingPriceChange(row.index, e.target.value)
              }
            />
          );
        },
      },

      {
        Header: "Total Cost Price",
        Cell: ({ row }) => {
          const store = storeMap?.[row?.original?.store_id];

          const total =
            Number(row.original?.cost_price || 0) *
            Number(row.original?.requested_quantity || 0);

          return formatCurrency({
            value: total,
            currencyCode: store?.currency_code,
          });
        },
      },

      {
        Header: "VAT Rate",
        accessor: "vat_rate",
        Cell: ({ cell: { value }, row }) => {
          const total =
            Number(row.original?.cost_price || 0) *
            Number(row.original?.requested_quantity || 0);

          return (
            <primaryComponents.VatPercentField
              name={`items.${row.index}.vat_rate`}
              control={control}
              value={value}
              type="number"
              min="0"
              max="100"
              step="0.01"
              dividedValue={total}
              floatError={false}
              onChange={({ percentValue }) =>
                updateItem(row.index, "vat_rate", percentValue)
              }
            />
          );
        },
      },
    ],
    [errors, storeMap, pOForm?.store_id],
  );

  const instance = useTable({
    columns,
    data: pOForm?.items || [],
  });

  const addProductToPO = (data) => {
    const rawCost =
      data?.cost_price !== undefined ? Number(data?.cost_price) : 0;
    const rawSelling =
      data?.pricings?.[0]?.price !== undefined
        ? Number(data?.pricings?.[0]?.price)
        : data?.selling_price !== undefined
          ? Number(data?.selling_price)
          : 0;

    const newData = {
      product_name: data?.name,
      product_id: data?.product_id,
      variant_id: data?.variant_id,
      requested_quantity: "",
      available_quantity: data?.stock_quantity,
      unit: data?.unit,
      cost_price: rawCost,
      base_cost_price: rawCost,
      selling_price: rawSelling,
      base_selling_price: rawSelling,
      percentage: "",
      store_id: data?.store_id,
      vat_rate: data?.vat_rate || storeMap?.[data?.store_id]?.vat,
    };

    setPOForm((prevState) => {
      const updated = [newData, ...(prevState?.items || [])];
      setValue("items", updated);
      return {
        ...prevState,
        items: updated,
      };
    });
  };

  const deleteRow = (index) => {
    setPOForm((prevState) => {
      const updatedItems = [...(prevState?.items || [])];
      updatedItems.splice(index, 1);
      setValue("items", updatedItems);
      if (clearErrors) {
        clearErrors("items");
      }
      return {
        ...prevState,
        items: updatedItems,
      };
    });
  };

  return {
    instance,
    addProductToPO,
  };
};
